<script setup>
import { computed, onUnmounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import CreateTicketModal from 'dashboard/routes/dashboard/tickets/CreateTicketModal.vue';
import CreateSalesOpportunityModal from 'dashboard/routes/dashboard/sales_pipeline/components/CreateSalesOpportunityModal.vue';
import CreateCalendarAppointmentModal from 'dashboard/routes/dashboard/calendar/CreateCalendarAppointmentModal.vue';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const { t } = useI18n();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const [showCreateTicketModal, toggleTicketModal] = useToggle(false);
const [showCreateOpportunityModal, toggleOpportunityModal] = useToggle(false);
const [showCreateCalendarAppointmentModal, toggleCalendarAppointmentModal] =
  useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const hasCrmFeature = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    currentAccountId.value,
    FEATURE_FLAGS.CRM
  )
);
const hasCalendarFeature = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    currentAccountId.value,
    FEATURE_FLAGS.CALENDAR
  )
);
const hasCalendarAppointment = computed(() => {
  if (currentChat.value.calendar_appointment) return true;

  return (currentChat.value.messages || []).some(message =>
    /\/calendar\?appointment_id=\d+/.test(message.content || '')
  );
});

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (hasCrmFeature.value && !currentChat.value.sales_opportunity) {
    items.push({
      icon: 'i-lucide-chart-no-axes-combined',
      label: t('SALES_PIPELINE.ADD_TO_PIPELINE'),
      action: 'create_sales_opportunity',
      value: 'create_sales_opportunity',
    });
  }

  if (hasCalendarFeature.value && !hasCalendarAppointment.value) {
    items.push({
      icon: 'i-lucide-calendar-plus',
      label: t('CONTACT_PANEL.SCHEDULE_APPOINTMENT'),
      action: 'create_calendar_appointment',
      value: 'create_calendar_appointment',
    });
  }

  if (!currentChat.value.ticket) {
    items.push({
      icon: 'i-lucide-book',
      label: t('CREATE_TICKET.CREATE'),
      action: 'create_ticket',
      value: 'create_ticket',
    });
  }

  return items;
});

const handleActionClick = ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'create_ticket') {
    toggleTicketModal();
  } else if (action === 'create_sales_opportunity') {
    toggleOpportunityModal();
  } else if (action === 'create_calendar_appointment') {
    toggleCalendarAppointmentModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <woot-modal
      v-if="showCreateTicketModal"
      v-model:show="showCreateTicketModal"
      :on-close="() => toggleTicketModal(false)"
    >
      <CreateTicketModal
        :conversation-id="currentChat.id"
        :on-close="() => toggleTicketModal(false)"
      />
    </woot-modal>
    <woot-modal
      v-if="showCreateOpportunityModal"
      v-model:show="showCreateOpportunityModal"
      :on-close="() => toggleOpportunityModal(false)"
    >
      <CreateSalesOpportunityModal
        :conversation-id="currentChat.id"
        :on-close="() => toggleOpportunityModal(false)"
      />
    </woot-modal>
    <woot-modal
      v-if="showCreateCalendarAppointmentModal"
      v-model:show="showCreateCalendarAppointmentModal"
      :on-close="() => toggleCalendarAppointmentModal(false)"
    >
      <CreateCalendarAppointmentModal
        :conversation-id="currentChat.id"
        :on-close="() => toggleCalendarAppointmentModal(false)"
      />
    </woot-modal>
  </div>
</template>
